/**
 * 通用状态标签组件
 *
 * 使用 antd Tag 组件渲染状态标签
 */

import { Tag } from 'antd';
import type React from 'react';
import type { StatusMap } from '../../types/common';

/**
 * StatusTag 组件属性
 */
export interface StatusTagProps<T extends string | number = string> {
  /** 当前状态值 */
  status: T | undefined | null;
  /** 状态映射表 */
  statusMap: StatusMap<T>;
  /** 未匹配状态时的默认文本，不设置则不渲染 */
  defaultText?: string;
  /** 未匹配状态时的默认颜色 */
  defaultColor?: string;
  /** 自定义类名 */
  className?: string;
  /** 自定义样式 */
  style?: React.CSSProperties;
}

/**
 * 通用状态标签组件
 *
 * 根据状态值从映射表中获取显示文本和颜色，渲染为 Ant Design Tag。
 *
 * @example
 * ```tsx
 * const STATUS_MAP: StatusMap<'active' | 'inactive'> = {
 *   active: { text: '启用', color: 'green' },
 *   inactive: { text: '禁用', color: 'red' },
 * };
 *
 * // 基础用法
 * <StatusTag status="active" statusMap={STATUS_MAP} />
 *
 * // 未知状态显示默认文本
 * <StatusTag status="unknown" statusMap={STATUS_MAP} defaultText="未知" defaultColor="default" />
 * ```
 */
export function StatusTag<T extends string | number = string>({
  status,
  statusMap,
  defaultText,
  defaultColor = 'default',
  className,
  style,
}: StatusTagProps<T>): React.ReactElement | null {
  if (status === undefined || status === null) {
    if (defaultText) {
      return (
        <Tag color={defaultColor} className={className} style={style}>
          {defaultText}
        </Tag>
      );
    }
    return null;
  }

  const config = statusMap[status as T];
  if (!config) {
    if (defaultText) {
      return (
        <Tag color={defaultColor} className={className} style={style}>
          {defaultText}
        </Tag>
      );
    }
    return null;
  }

  return (
    <Tag
      color={config.color}
      icon={config.icon}
      className={className}
      style={style}
    >
      {config.text}
    </Tag>
  );
}
